import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";
import { useRoles } from "@/hooks/useRoles";
import { diffRecords, recordAudit } from "@/lib/audit";
import { roleIdsToNames } from "@/lib/role-formatting";
import { supabase } from "@/lib/supabase";
import type { Tables } from "@/types/database.types";
import {
  excludeLostProjectAssignments,
  shouldIncludeEmployeeInMakersList,
} from "@/utils/makersUtils";
import { useCallback, useEffect, useState } from "react";

export type EmployeeAssignment = Tables<"project_assignments"> & {
  project: {
    id: string;
    name: string | null;
    client: string | null;
    color: string | null;
    start_date: string | null;
    num_weeks: number | null;
    // Deal stage embedded from deal_stages (joined by id); the kind drives the Lost
    // filter. Optional because only the makers query selects it — board-side code
    // that builds this shape (e.g. timeline allocations) doesn't carry it.
    deal_stage?: { name: string | null; kind: string | null } | null;
  } | null;
};

export type EmployeeSummary = Tables<"employees"> & {
  office: {
    name: string | null;
  } | null;
  weeklyCapacityHours: number;
  assignments: EmployeeAssignment[];
};

/** A role id (uuid). */
export type EmployeeRole = string;

export interface UseMakersBoardDataResult {
  employees: EmployeeSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refreshSilently: () => Promise<void>;
  updateCapacityOptimistically: (employeeId: string, newCapacity: number) => void;
  updateEmployeeCapacity: (employeeId: string, newCapacity: number) => Promise<void>;
  updateEmployeeName: (employeeId: string, newName: string) => Promise<void>;
  updateEmployeeNickname: (employeeId: string, nickname: string | null) => Promise<void>;
  updateEmployeeRoles: (employeeId: string, roles: EmployeeRole[]) => Promise<void>;
  updateNameOptimistically: (employeeId: string, newName: string) => void;
  updateNicknameOptimistically: (employeeId: string, nickname: string | null) => void;
  updateRolesOptimistically: (employeeId: string, roles: EmployeeRole[]) => void;
}


export function useMakersBoardData(): UseMakersBoardDataResult {
  const [employees, setEmployees] = useState<EmployeeSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  // Used to record role audit entries by display name rather than raw id; see
  // updateEmployeeRoles. (In the detached FindPeopleModal root this resolves to
  // the empty context, but that path never calls updateEmployeeRoles.)
  const { formatRole } = useRoles();

  const performFetch = useCallback(async (silent: boolean) => {
    if (!silent) setLoading(true);

    // Get all active employees OR employees with assignments
    // First, get active employees
    const { data: activeEmployeesData, error: activeError } = await supabase
      .from("employees")
      .select(
        `
        id,
        name,
        nickname,
        email,
        role,
        office_id,
        is_active,
        employee_number,
        hire_date,
        image_url,
        weekly_capacity_hours,
        created_at,
        updated_at,
        office:offices ( name )
      `
      )
      .eq("is_active", true)
      .order("name", { ascending: true });

    if (activeError) {
      setError(activeError.message ?? "Failed to load employees");
      setEmployees([]);
      setLoading(false);
      return;
    }

    // Get all employee IDs that have assignments
    const { data: assignmentEmployees, error: assignmentError } = await supabase
      .from("assignment_employees")
      .select("employee_id");

    if (assignmentError) {
      setError(assignmentError.message ?? "Failed to load assignment employees");
      setEmployees([]);
      setLoading(false);
      return;
    }

    // Collect all employee IDs with assignments
    const employeeIdsWithAssignments = new Set<string>();
    (assignmentEmployees || []).forEach((ae: any) => {
      if (ae.employee_id) employeeIdsWithAssignments.add(ae.employee_id);
    });

    // Get inactive employees who have assignments
    const inactiveEmployeeIds = Array.from(employeeIdsWithAssignments).filter(
      (id) => !activeEmployeesData?.some((emp) => emp.id === id)
    );

    let inactiveEmployeesData: any[] = [];
    if (inactiveEmployeeIds.length > 0) {
      const { data: inactiveData, error: inactiveError } = await supabase
        .from("employees")
        .select(
          `
          id,
          name,
          nickname,
          email,
          role,
          office_id,
          is_active,
          employee_number,
          hire_date,
          image_url,
          weekly_capacity_hours,
          created_at,
          updated_at,
          office:offices ( name )
        `
        )
        .in("id", inactiveEmployeeIds)
        .order("name", { ascending: true });

      if (!inactiveError && inactiveData) {
        inactiveEmployeesData = inactiveData;
      }
    }

    // Combine active and inactive employees with assignments
    const employeesData = [
      ...(activeEmployeesData || []),
      ...inactiveEmployeesData,
    ];

    if (employeesData.length === 0) {
      setEmployees([]);
      setError(null);
      setLoading(false);
      return;
    }

    // Get all assignments that have employees assigned via assignment_employees junction table
    const { data: assignmentsData, error: assignmentsError } = await supabase
      .from("project_assignments")
      .select(
        `
        id,
        project_id,
        required_role_id,
        weekly_hours_allocated,
        is_confirmed,
        notes,
        created_at,
        updated_at,
        project:projects!inner ( id, name, client, color, deal_stage_id, deal_stage:deal_stages ( name, kind ), start_date, num_weeks ),
        assignment_employees (
          employee:employees ( id )
        )
      `
      );

    if (assignmentsError) {
      setError(assignmentsError.message ?? "Failed to load assignments");
      setEmployees([]);
      setLoading(false);
      return;
    }

    // Build a map of employee_id -> assignments
    const assignmentsByEmployee = new Map<string, EmployeeAssignment[]>();

    excludeLostProjectAssignments(assignmentsData ?? [])
      .forEach((assignment: any) => {
        const assignmentEmployees = assignment.assignment_employees || [];

        // Include assignments that have this employee in the junction table
        assignmentEmployees.forEach((ae: any) => {
          const employeeId = ae.employee?.id;
          if (employeeId) {
            if (!assignmentsByEmployee.has(employeeId)) {
              assignmentsByEmployee.set(employeeId, []);
            }

            const { assignment_employees, ...assignmentFields } = assignment;
            // Avoid duplicates
            const existing = assignmentsByEmployee.get(employeeId)!;
            if (!existing.some(a => a.id === assignment.id)) {
              existing.push({
                ...assignmentFields,
                project: assignment.project ?? null,
              });
            }
          }
        });
      });

    // Combine employees with their assignments
    const normalized: EmployeeSummary[] = (employeesData ?? [])
      .map((employee) => {
        const { office, ...employeeFields } = employee;
        return {
          ...employeeFields,
          office: office ?? null,
          weeklyCapacityHours: employee.weekly_capacity_hours ?? 40,
          assignments: assignmentsByEmployee.get(employee.id) ?? [],
        };
      })
      .filter((employee) => shouldIncludeEmployeeInMakersList(employee));

    setEmployees(normalized);
    setError(null);
    setLoading(false);
  }, []);

  const fetchEmployees = useCallback(() => performFetch(false), [performFetch]);
  const refreshSilently = useCallback(() => performFetch(true), [performFetch]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Refetch silently when any of these tables change remotely; loading spinner stays put.
  useRealtimeRefresh(
    ["employees", "project_assignments", "assignment_employees", "projects"],
    refreshSilently,
    // Our own edits (capacity/role/name) update local state optimistically, and
    // add/delete trigger an explicit refresh — so skip the redundant refetch that
    // the echoed realtime event would otherwise cause (which re-renders the table).
    { skipRefetchOnSelfEdit: true }
  );

  // Optimistically update an employee's capacity in the local state
  const updateCapacityOptimistically = useCallback((employeeId: string, newCapacity: number) => {
    setEmployees((prevEmployees) =>
      prevEmployees.map((employee) =>
        employee.id === employeeId ? { ...employee, weekly_capacity_hours: newCapacity, weeklyCapacityHours: newCapacity } : employee
      )
    );
  }, []);

  async function updateEmployeeCapacity(employeeId: string, newCapacity: number) {
    markSelfEdit("employees", employeeId);
    const oldEmployee = employees.find((e) => e.id === employeeId);
    const { error } = await supabase
      .from("employees")
      .update({ weekly_capacity_hours: newCapacity })
      .eq("id", employeeId);
    if (!error) {
      recordAudit({
        table: "employees",
        recordId: employeeId,
        operation: "UPDATE",
        changes: diffRecords(
          { weekly_capacity_hours: oldEmployee?.weekly_capacity_hours ?? null },
          { weekly_capacity_hours: newCapacity }
        ),
        recordLabel: oldEmployee?.name ?? null,
        officeId: oldEmployee?.office_id ?? null,
      });
    }
  }

  const updateRolesOptimistically = useCallback((employeeId: string, roles: EmployeeRole[]) => {
    setEmployees((prevEmployees) =>
      prevEmployees.map((employee) =>
        employee.id === employeeId ? { ...employee, role: roles } : employee
      )
    );
  }, []);

  async function updateEmployeeRoles(employeeId: string, roles: EmployeeRole[]) {
    markSelfEdit("employees", employeeId);
    const oldEmployee = employees.find((e) => e.id === employeeId);
    const { error } = await supabase
      .from("employees")
      .update({ role: roles })
      .eq("id", employeeId);
    if (error) throw error;
    const oldRoles = oldEmployee?.role ?? null;
    const changes = diffRecords({ role: oldRoles }, { role: roles });
    if (changes.role) {
      changes.role = {
        old: roleIdsToNames(oldRoles, formatRole),
        new: roleIdsToNames(roles, formatRole),
      };
    }
    recordAudit({
      table: "employees",
      recordId: employeeId,
      operation: "UPDATE",
      changes,
      recordLabel: oldEmployee?.name ?? null,
      officeId: oldEmployee?.office_id ?? null,
    });
  }

  const updateNameOptimistically = useCallback((employeeId: string, newName: string) => {
    setEmployees((prevEmployees) =>
      prevEmployees.map((employee) =>
        employee.id === employeeId ? { ...employee, name: newName } : employee
      )
    );
  }, []);

  async function updateEmployeeName(employeeId: string, newName: string) {
    markSelfEdit("employees", employeeId);
    const oldEmployee = employees.find((e) => e.id === employeeId);
    const { error } = await supabase
      .from("employees")
      .update({ name: newName })
      .eq("id", employeeId);
    if (error) throw error;
    recordAudit({
      table: "employees",
      recordId: employeeId,
      operation: "UPDATE",
      changes: diffRecords({ name: oldEmployee?.name ?? null }, { name: newName }),
      recordLabel: newName,
      officeId: oldEmployee?.office_id ?? null,
    });
  }

  const updateNicknameOptimistically = useCallback(
    (employeeId: string, nickname: string | null) => {
      setEmployees((prevEmployees) =>
        prevEmployees.map((employee) =>
          employee.id === employeeId ? { ...employee, nickname } : employee
        )
      );
    },
    [],
  );

  async function updateEmployeeNickname(employeeId: string, nickname: string | null) {
    markSelfEdit("employees", employeeId);
    const oldEmployee = employees.find((employee) => employee.id === employeeId);
    const { error } = await supabase
      .from("employees")
      .update({ nickname })
      .eq("id", employeeId);
    if (error) throw error;
    recordAudit({
      table: "employees",
      recordId: employeeId,
      operation: "UPDATE",
      changes: diffRecords(
        { nickname: oldEmployee?.nickname ?? null },
        { nickname },
      ),
      recordLabel: nickname || oldEmployee?.name || null,
      officeId: oldEmployee?.office_id ?? null,
    });
  }

  return {
    employees,
    loading,
    error,
    refresh: fetchEmployees,
    refreshSilently,
    updateCapacityOptimistically,
    updateEmployeeCapacity,
    updateEmployeeName,
    updateEmployeeNickname,
    updateEmployeeRoles,
    updateNameOptimistically,
    updateNicknameOptimistically,
    updateRolesOptimistically,
  };
}
